import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { Constant } from '../config/constants';
import { IUser } from '../core/user/user.model';
import { Privilege } from '../model/entity/privilege';
import { UserRoute } from 'app/model/entity/user-route';
import { RouteSetting } from 'app/model/entity/route-setting';

@Injectable({
  providedIn: 'root'
})
/**
 * Class UserService
 */
export class UserService {
  private dataChangedTabRoute = new BehaviorSubject<boolean>(false);
  private resetTrigger = new Subject<void>(); // Tín hiệu để gọi resetToInitialState
  private saveTrigger = new Subject<void>();
  /**
   * user url
   */
  private userUrl = Constant.BACKEND_URL + Constant.UAA_SERVICE_URL + 'api/users';
  private registerUrl = Constant.BACKEND_URL + Constant.UAA_SERVICE_URL + 'api/register';
  /**
   * privilege url
   */
  private privilegeUrl = Constant.BACKEND_URL + Constant.UAA_SERVICE_URL + 'api/users/privileges';
  /**
   * route url
   */
  private routeUrl = Constant.BACKEND_URL + Constant.UAA_SERVICE_URL + 'api/route';

  constructor(private http: HttpClient) {}

  /**
   * Get list of users
   */
  getUsers(): Observable<IUser[]> {
    return this.http.get<IUser[]>(`${this.userUrl}/getall`);
  }

  /**
   * add a new user
   * @param {User} user new user
   */
  addUser(user: IUser): Observable<IUser> {
    return this.http.post<IUser>(this.registerUrl, user);
  }

  /**
   * update selected user
   * @param {User} user selected user
   */
  updateUser(user: IUser): Observable<IUser> {
    return this.http.put<IUser>(this.userUrl, user);
  }

  /**
   * delete selected user
   * @param {User} user selected user
   */
  deleteUser(user: IUser) {
    return this.http.delete(`${this.userUrl}/${user.sub}`);
  }

  /**
   * Get user by id
   * @param userId user ID
   */
  getUserById(userId: string): Observable<IUser> {
    return this.http.get<IUser>(`${this.userUrl}/${userId}`);
  }

  /**
   * get privileges by user role
   *
   * @param roleId  roleId
   * @param isUserRoot
   * @returns
   */
  getPrivilegesByUserRole(roleId: Number, isUserRoot: boolean): Observable<Privilege[]> {
    const params = new HttpParams().set('roleId', roleId + '').set(Constant.IS_USER_ROOT, isUserRoot + '');
    return this.http.get<Privilege[]>(`${this.privilegeUrl}/get-privileges-by-user-role`, { params });
  }

  /**
   * update privilege
   * @param {Privilege} privilege privilege
   */
  updatePrivilege(privileges: Privilege[]): Observable<Privilege[]> {
    return this.http.post<Privilege[]>(this.privilegeUrl, privileges);
  }

  /**
   * get all privileges
   *
   * @returns
   */
  public getAllPrivileges(): Observable<Privilege[]> {
    return this.http.get<Privilege[]>(`${this.privilegeUrl}/get-all-privileges`);
  }

  getTokenCognito(code: string, csrfToken: string): Observable<any> {
    const url = `${Constant.BACKEND_URL}auth/get-cognito-token`;
    const params = new HttpParams().set('code', code);
    return this.http.get(url, { params });
  }

  /**
   * get all routes
   *
   * @returns
   */
  public getAllRoutes(): Observable<UserRoute[]> {
    return this.http.get<UserRoute[]>(`${this.routeUrl}/get-all-routes`);
  }

  /**
   *
   * import route excel
   *
   * @returns
   */
  importExcelRoutes(file: File): Observable<UserRoute[]> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('size', `${file.size}`);
    return this.http.post<UserRoute[]>(`${this.routeUrl}/import-excel-routes`, formData);
  }

  /**
   * export template
   * @param template
   * @returns
   */
  public exportExcelRoutes(): Observable<any> {
    return this.http.post(
      `${this.routeUrl}/export-excel-routes`,
      {},
      {
        responseType: 'blob',
        observe: 'response'
      }
    );
  }

  /**
   * get all routes
   *
   * @returns
   */
  public getAllRouteSettings(): Observable<RouteSetting[]> {
    return this.http.get<RouteSetting[]>(`${this.routeUrl}/get-all-route-settings`);
  }

  /**
   * update route setting
   * @param {RouteSetting} routeSettings route setting
   */
  public updateRouteSettings(routeSettings: RouteSetting[]): Observable<RouteSetting[]> {
    return this.http.post<RouteSetting[]>(`${this.routeUrl}/save-route-settings`, routeSettings);
  }
}
